import roundMid from './roundMid';
import { BidAskMidFn } from '../types/BidAskMidFn';
import SymbolParser from '../../symbol/SymbolParser';
import decimal from '../../../math/decimal';

import { LegType } from '@oai-frontend-monorepo/types';
import parseLegTypeFromTradeRef from '../../strategy/parseLegTypeFromTradeRef';
import { strategyIsCredit } from '../../strategy/strategyIsCredit';
import getSpreadWidth from '../getSpreadWidth';
import Decimal from 'decimal.js';

const calculateTwoLegSpreadQuote: BidAskMidFn = ({
  legs: allContracts,
  strategy,
  isSell,
}) => {
  if (!allContracts?.length || !strategy) {
    return undefined;
  }

  // get the short leg contract
  const shortLeg = allContracts.find((l) => {
    const type = parseLegTypeFromTradeRef({
      symbol: l.symbol,
      tradeRef: {
        symbols: allContracts.map((l) => l.symbol),
        strategy,
        underlyingSymbol: '',
      },
    });
    return type === LegType.SHORT_CALL || type === LegType.SHORT_PUT;
  });

  // get the long leg contract
  const longLeg = allContracts.find((l) => {
    const type = parseLegTypeFromTradeRef({
      symbol: l.symbol,
      tradeRef: {
        symbols: allContracts.map((l) => l.symbol),
        strategy,
        underlyingSymbol: '',
      },
    });
    return type === LegType.LONG_CALL || type === LegType.LONG_PUT;
  });

  // short leg pricing
  const shortCallBid = decimal(shortLeg?.bid);
  const shortCallAsk = decimal(shortLeg?.ask);
  const shortLegOpen = decimal(shortLeg?.open);
  const shortLegLast = decimal(shortLeg?.last);
  const shortLegClose = decimal(shortLeg?.close);
  const shortLegPrevClose = decimal(shortLeg?.prevclose);

  // long leg pricing
  const longCallBid = decimal(longLeg?.bid);
  const longCallAsk = decimal(longLeg?.ask);
  const longLegOpen = decimal(longLeg?.open);
  const longLegLast = decimal(longLeg?.last);
  const longLegClose = decimal(longLeg?.close);
  const longLegPrevClose = decimal(longLeg?.prevclose);

  // short mid
  const shortLegMid = decimal(shortLeg?.ask)
    ?._plus?.(shortLeg?.bid)
    ?.dividedBy(2);

  // long mid
  const longLegMid = decimal(longLeg?.ask)?._plus?.(longLeg?.bid)?.dividedBy(2);

  // strict undefined checks
  if (
    shortCallBid === undefined ||
    shortCallAsk === undefined ||
    longCallBid === undefined ||
    longCallAsk === undefined ||
    shortLegMid === undefined ||
    longLegMid === undefined
  ) {
    return undefined;
  }

  // parser used for underlying symbol and SPX check
  const parser = new SymbolParser(allContracts[0].symbol);

  // spx specific rounding
  const isSPX = parser.isSPX;
  const ROUNDING_VALUE = isSPX ? 0.05 : 0.01;

  // is it a credit strategy
  const isCredit = strategyIsCredit(strategy);

  // calculate ask, bid, and mid prices
  const ask = shortCallAsk?.minus(longCallBid).abs();
  const bid = shortCallBid.minus(longCallAsk).abs();
  const mid = shortLegMid.minus(longLegMid).abs();

  // get max value via spread width
  const spreadWidth = (getSpreadWidth({
    strategy,
    underlyingSymbol: parser.underlyingSymbol as string,
    symbols: allContracts.map((l) => l.symbol),
  }) ?? 0) as unknown as Decimal;

  // use spread width if mid is greater than it
  const cappedMid: Decimal = mid.greaterThan(spreadWidth)
    ? new Decimal(spreadWidth)
    : mid;

  // derive last
  const last =
    shortLegLast && longLegLast ? shortLegLast.minus(longLegLast) : undefined;

  // get the opening price if the option was opened same day
  const open =
    shortLegOpen && longLegOpen
      ? shortLegOpen.minus(longLegOpen).abs()
      : undefined;

  // derive close price
  const close =
    shortLegClose && longLegClose
      ? shortLegClose.minus(longLegClose)
      : undefined;

  // derive previous close price
  const prevClose =
    shortLegPrevClose && longLegPrevClose
      ? shortLegPrevClose.minus(longLegPrevClose)
      : undefined;

  return {
    ask: isCredit ? ask.toNumber() : bid.toNumber(),
    bid: isCredit ? bid.toNumber() : ask.toNumber(),
    mid:
      roundMid({ mid: cappedMid, isSell, strategy })
        ?.toNearest(ROUNDING_VALUE)
        .toNumber() || 0.01,
    open:
      roundMid({ mid: open, isSell, strategy })
        ?.toNearest(ROUNDING_VALUE)
        .toNumber() || 0.01,
    last:
      roundMid({ mid: last, isSell, strategy })
        ?.toNearest(ROUNDING_VALUE)
        .toNumber() || 0.01,
    close:
      roundMid({ mid: close, isSell, strategy })
        ?.toNearest(ROUNDING_VALUE)
        .toNumber() || 0.01,
    prevClose:
      roundMid({ mid: prevClose, isSell, strategy })
        ?.toNearest(ROUNDING_VALUE)
        .toNumber() || 0.01,
  };
};

export default calculateTwoLegSpreadQuote;
